import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Log de l'URL API en développement pour le débogage
if (process.env.NODE_ENV === 'development') {
  console.log('API URL configurée:', API_URL);
}

// Configuration axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token
api.interceptors.request.use(
  (config) => {
    // Si c'est un FormData, ne pas définir Content-Type (axios le fera automatiquement avec le boundary)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Déterminer quel token utiliser selon le contexte
    const currentPath = window.location.pathname;
    let token = null;
    
    if (currentPath.includes('/admin')) {
      token = localStorage.getItem('admin_token');
    } else if (currentPath.includes('/recolteur')) {
      token = localStorage.getItem('recolteur_token');
    } else if (currentPath.includes('/gerer')) {
      token = localStorage.getItem('gerer_token');
    } else if (currentPath.includes('/famille')) {
      token = localStorage.getItem('famille_token');
    } else if (currentPath.includes('/finances')) {
      token = localStorage.getItem('finances_token');
    } else if (currentPath.includes('/membre')) {
      // Les membres n'utilisent pas de token JWT, ils utilisent le numéro matricule
      token = null;
    } else {
      // Par défaut, essayer d'abord admin, puis récolteur, puis gerer, puis famille, puis finances
      token = localStorage.getItem('admin_token') || localStorage.getItem('recolteur_token') || localStorage.getItem('gerer_token') || localStorage.getItem('famille_token') || localStorage.getItem('finances_token');
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Gérer les erreurs 429 (Too Many Requests) de manière silencieuse
    // Ces erreurs sont temporaires et ne doivent pas faire planter l'application
    if (error.response?.status === 429) {
      console.warn('⚠️ Rate limit atteint (429). La requête sera réessayée automatiquement.');
      // Ne pas rejeter l'erreur immédiatement, permettre aux composants de gérer le retry
      // L'erreur sera quand même propagée mais de manière contrôlée
    }
    
    if (error.response?.status === 401) {
      // Ne pas rediriger si on est déjà sur une page de login
      // La redirection sera gérée par le composant de login lui-même
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        // Supprimer uniquement les tokens correspondants au contexte
        if (currentPath.includes('/admin')) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/login-admin';
        } else if (currentPath.includes('/recolteur')) {
          localStorage.removeItem('recolteur_token');
          localStorage.removeItem('recolteur_user');
          window.location.href = '/login-recolteur';
        } else if (currentPath.includes('/gerer')) {
          localStorage.removeItem('gerer_token');
          localStorage.removeItem('gerer_user');
          window.location.href = '/login-gerer';
        } else if (currentPath.includes('/famille')) {
          localStorage.removeItem('famille_token');
          localStorage.removeItem('famille_user');
          window.location.href = '/login-famille';
        } else if (currentPath.includes('/finances')) {
          localStorage.removeItem('finances_token');
          localStorage.removeItem('finances_user');
          window.location.href = '/login-finances';
        } else if (currentPath.includes('/membre')) {
          // Les membres n'utilisent pas de token, donc on ne supprime que leur session
          localStorage.removeItem('membre_user');
          window.location.href = '/login-membre';
        } else {
          // Si on ne sait pas, supprimer uniquement les tokens (pas les membres)
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          localStorage.removeItem('recolteur_token');
          localStorage.removeItem('recolteur_user');
          localStorage.removeItem('gerer_token');
          localStorage.removeItem('gerer_user');
          localStorage.removeItem('famille_token');
          localStorage.removeItem('famille_user');
          localStorage.removeItem('finances_token');
          localStorage.removeItem('finances_user');
          // Ne pas supprimer 'membre_user' car ce n'est pas lié à un token
          window.location.href = '/login-recolteur';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const loginAdmin = (username, password) => 
  api.post('/auth/login', { username, password });

export const loginRecolteur = (username, password) => 
  api.post('/auth/login-recolteur', { username, password });

export const loginGerer = (username, password) => 
  api.post('/auth/login-gerer', { username, password });

export const loginFamille = (username, password) => 
  api.post('/auth/login-famille', { username, password });

export const loginFinances = (username, password) => 
  api.post('/auth/login-finances', { username, password });

export const verifyToken = () => 
  api.get('/auth/verify');

// Membres
export const createMembre = (formData) => 
  api.post('/membres', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

export const getMembres = () => 
  api.get('/membres');

export const getMembre = (id) => 
  api.get(`/membres/${id}`);

export const getMembreByMatricule = (matricule) => 
  api.get(`/membres/matricule/${matricule}`);

export const updateMembre = (id, formData) => 
  api.put(`/membres/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

export const deleteMembre = (id) => 
  api.delete(`/membres/${id}`);

export const regenererCodeBarres = (id) => 
  api.post(`/membres/${id}/regenerer-code-barres`);

// Familles
export const getFamilles = () => 
  api.get('/familles');

export const getFamille = (id) => 
  api.get(`/familles/${id}`);

export const createFamille = (formData) => 
  api.post('/familles', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

export const updateFamille = (id, formData) => 
  api.put(`/familles/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

export const deleteFamille = (id) => 
  api.delete(`/familles/${id}`);

export const regenererNumeroFamille = (id) => 
  api.post(`/familles/${id}/regenerer-numero`);

// Membres de famille
export const createMembreFamille = (familleId, data) => 
  api.post(`/familles/${familleId}/membres`, data);

export const updateMembreFamille = (familleId, membreId, data) => 
  api.put(`/familles/${familleId}/membres/${membreId}`, data);

export const deleteMembreFamille = (familleId, membreId) => 
  api.delete(`/familles/${familleId}/membres/${membreId}`);

// Enfants des membres
export const getEnfantsMembre = (membreId) =>
  api.get(`/membres/${membreId}/enfants`);

export const createEnfantMembre = (membreId, data) => {
  // Si data est FormData, créer une nouvelle instance axios sans le header Content-Type par défaut
  if (data instanceof FormData) {
    const config = {
      headers: {}
    };
    // Ne pas définir Content-Type, axios le fera automatiquement avec le boundary
    return api.post(`/membres/${membreId}/enfants`, data, config);
  }
  return api.post(`/membres/${membreId}/enfants`, data);
};

export const updateEnfantMembre = (membreId, enfantId, data) => {
  // Si data est FormData, créer une nouvelle instance axios sans le header Content-Type par défaut
  if (data instanceof FormData) {
    const config = {
      headers: {}
    };
    // Ne pas définir Content-Type, axios le fera automatiquement avec le boundary
    return api.put(`/membres/${membreId}/enfants/${enfantId}`, data, config);
  }
  return api.put(`/membres/${membreId}/enfants/${enfantId}`, data);
};

export const deleteEnfantMembre = (membreId, enfantId) =>
  api.delete(`/membres/${membreId}/enfants/${enfantId}`);

// Cotisations
export const createCotisation = (data) => 
  api.post('/cotisations', data);

export const getCotisations = () => 
  api.get('/cotisations');

export const getCotisationsByMembre = (membreId) =>
  api.get(`/cotisations/membre/${membreId}`);

// Routes publiques pour les membres (utilisent le numéro matricule)
export const getCotisationsByMembrePublic = (numeroMatricule) =>
  api.get(`/public/membre/${numeroMatricule}/cotisations`);

export const getCotisationsImpayeesByMois = (mois, annee, membreId = null) => {
  const url = `/cotisations/impayees/${mois}/${annee}`;
  const params = membreId ? { membre_id: membreId } : {};
  return api.get(url, { params });
};

export const updateCotisation = (id, data) => 
  api.put(`/cotisations/${id}`, data);

export const deleteCotisation = (id) => 
  api.delete(`/cotisations/${id}`);

// Paiements
export const createPaiement = (data) => 
  api.post('/paiements', data);

export const getPaiements = () => 
  api.get('/paiements');

export const getPaiementsByMembre = (membreId) => 
  api.get(`/paiements/membre/${membreId}`);

// Routes publiques pour les membres (utilisent le numéro matricule)
export const getPaiementsByMembrePublic = (numeroMatricule) =>
  api.get(`/public/membre/${numeroMatricule}/paiements`);

export const getPaiementByRecu = (numeroRecu) => 
  api.get(`/paiements/recu/${numeroRecu}`);

export const deletePaiement = (id) => 
  api.delete(`/paiements/${id}`);

// Admin
export const getStats = (mois, annee, jour = null) => {
  const params = new URLSearchParams();
  if (mois) params.append('mois', mois);
  if (annee) params.append('annee', annee);
  if (jour !== null) params.append('jour', jour);
  const queryString = params.toString();
  const url = `/admin/stats${queryString ? `?${queryString}` : ''}`;
  console.log('📡 Appel API getStats:', url);
  return api.get(url);
};

export const getStatsJournalieres = () => 
  api.get('/admin/stats/journalieres');

export const getMembresInscritsAujourdhui = () => 
  api.get('/admin/membres-inscrits-aujourdhui');

export const getCotisationsPayeesAujourdhui = () => 
  api.get('/admin/cotisations-payees-aujourdhui');

export const getPaiementsGlobaux = (mois, annee, date = null) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  else {
    if (mois) params.append('mois', mois);
    if (annee) params.append('annee', annee);
  }
  const queryString = params.toString();
  return api.get(`/admin/paiements-globaux${queryString ? `?${queryString}` : ''}`);
};

export const getCotisationsImpayees = (mois, annee) => {
  const params = new URLSearchParams();
  if (mois) params.append('mois', mois);
  if (annee) params.append('annee', annee);
  const queryString = params.toString();
  return api.get(`/admin/cotisations-impayees${queryString ? `?${queryString}` : ''}`);
};

export const getCotisationsPayees = (mois, annee) => {
  const params = new URLSearchParams();
  if (mois) params.append('mois', mois);
  if (annee) params.append('annee', annee);
  const queryString = params.toString();
  return api.get(`/admin/cotisations-payees${queryString ? `?${queryString}` : ''}`);
};

// Statistiques des familles
export const getStatsFamillesJournalieres = () => 
  api.get('/admin/stats/familles/journalieres');

export const getStatsFamillesMensuelles = (mois, annee) => {
  const params = new URLSearchParams();
  if (mois) params.append('mois', mois);
  if (annee) params.append('annee', annee);
  return api.get(`/admin/stats/familles/mensuelles?${params.toString()}`);
};

export const getStatsFamillesGlobales = () => 
  api.get('/admin/stats/familles/globales');

export const getStatsEvolution = (nbMois = 6) => 
  api.get(`/admin/stats/evolution?nbMois=${nbMois}`);

export const getAdmins = () => 
  api.get('/admin/admins');

export const createAdmin = (data) => 
  api.post('/admin/admins', data);

export const updateAdmin = (id, data) => 
  api.put(`/admin/admins/${id}`, data);

export const deleteAdmin = (id) => 
  api.delete(`/admin/admins/${id}`);

// Récolteurs
export const getRecolteurs = () => 
  api.get('/admin/recolteurs');

// Statistiques récolteur (pour le récolteur lui-même)
export const getStatsRecolteur = () => 
  api.get('/recolteur/stats');

// Statistiques détaillées d'un récolteur (pour l'admin)
export const getRecolteurStats = (recolteurId, date = null) => {
  const params = date ? { params: { date } } : {};
  return api.get(`/admin/recolteurs/${recolteurId}/stats`, params);
};

export const createRecolteur = (data) => 
  api.post('/admin/recolteurs', data);

export const updateRecolteur = (id, data) => 
  api.put(`/admin/recolteurs/${id}`, data);

export const deleteRecolteur = (id) => 
  api.delete(`/admin/recolteurs/${id}`);

// Notifications récolteur
export const getRecolteurNotifications = () => 
  api.get('/recolteur/notifications');

export const getRecolteurNotificationsCount = () => 
  api.get('/recolteur/notifications/count-non-lues');

export const marquerRecolteurNotificationLue = (id) => 
  api.put(`/recolteur/notifications/${id}/lue`);

export const marquerToutesRecolteurNotificationsLues = () => 
  api.put('/recolteur/notifications/marquer-toutes-lues');

// Gestionnaires (Gerer)
export const getGerers = () =>
  api.get('/admin/gerers');

export const createGerer = (data) =>
  api.post('/admin/gerers', data);

export const updateGerer = (id, data) =>
  api.put(`/admin/gerers/${id}`, data);

export const deleteGerer = (id) =>
  api.delete(`/admin/gerers/${id}`);

// Finances
export const getFinances = () =>
  api.get('/admin/finances');

export const createFinances = (data) =>
  api.post('/admin/finances', data);

export const updateFinances = (id, data) =>
  api.put(`/admin/finances/${id}`, data);

export const deleteFinances = (id) =>
  api.delete(`/admin/finances/${id}`);

// Centres de santé
export const getCentresSante = () => 
  api.get('/admin/centres-sante');

export const createCentreSante = (data) => 
  api.post('/admin/centres-sante', data);

export const updateCentreSante = (id, data) => 
  api.put(`/admin/centres-sante/${id}`, data);

export const deleteCentreSante = (id) => 
  api.delete(`/admin/centres-sante/${id}`);

// Déclaration des centres de santé aux membres
export const declarerCentres = (data) => 
  api.post('/admin/declarer-centres', data);

export const declarerCentresFamilles = (data) => 
  api.post('/admin/declarer-centres-familles', data);

// Montants mensuels
export const getMontantsMensuels = () => api.get('/montants');

// Montants mensuels des familles
export const getMontantsMensuelsFamilles = () => api.get('/montants-familles');
export const getMontantMensuelFamille = (mois, annee) => api.get(`/montants-familles/${mois}/${annee}`);
export const createMontantMensuelFamille = (data) => api.post('/montants-familles', data);
export const updateMontantMensuelFamille = (id, data) => api.put(`/montants-familles/${id}`, data);
export const getMontantMensuel = (mois, annee) => api.get(`/montants/${mois}/${annee}`);
export const getStatsCotisations = (mois, annee) => {
  const params = new URLSearchParams();
  if (mois) params.append('mois', mois);
  if (annee) params.append('annee', annee);
  return api.get(`/montants/stats/cotisations?${params.toString()}`);
};
export const createMontantMensuel = (data) => api.post('/montants', data);
export const updateMontantMensuel = (id, data) => api.put(`/montants/${id}`, data);
export const deleteMontantMensuel = (id) => api.delete(`/montants/delete/${id}`);

// Cotisations et paiements des familles
export const getCotisationsFamilles = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.famille_id) queryParams.append('famille_id', params.famille_id);
  if (params.mois) queryParams.append('mois', params.mois);
  if (params.annee) queryParams.append('annee', params.annee);
  if (params.statut) queryParams.append('statut', params.statut);
  return api.get(`/familles/cotisations?${queryParams.toString()}`);
};

export const getCotisationFamille = (id) => api.get(`/familles/cotisations/${id}`);

export const createCotisationFamille = (data) => api.post('/familles/cotisations', data);

export const getPaiementsFamilles = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.famille_id) queryParams.append('famille_id', params.famille_id);
  if (params.date_debut) queryParams.append('date_debut', params.date_debut);
  if (params.date_fin) queryParams.append('date_fin', params.date_fin);
  return api.get(`/familles/paiements?${queryParams.toString()}`);
};

export const getPaiementFamille = (id) => api.get(`/familles/paiements/${id}`);

export const createPaiementFamille = (data) => api.post('/familles/paiements', data);

export const deletePaiementFamille = (id) => api.delete(`/familles/paiements/${id}`);

// Public
export const verifierMatricule = (numeroMatricule) => 
  api.get(`/public/verifier/${numeroMatricule}`);

export const verifierFamille = (numeroFamille) => 
  api.get(`/public/verifier-famille/${numeroFamille}`);

export const loginMembre = (numeroMatricule, password) => {
  // Log des valeurs reçues dans la fonction API
  console.log('📡 api.js - loginMembre appelé avec:');
  console.log('   - numeroMatricule:', JSON.stringify(numeroMatricule), '(type:', typeof numeroMatricule, ')');
  console.log('   - password:', JSON.stringify(password), '(type:', typeof password, ')');
  console.log('   - password length:', password?.length);
  
  // S'assurer que password est bien une string
  const passwordStr = String(password || '').toUpperCase().trim();
  const numeroMatriculeStr = String(numeroMatricule || '').toUpperCase().trim();
  
  console.log('📡 api.js - Valeurs après conversion:');
  console.log('   - numeroMatriculeStr:', JSON.stringify(numeroMatriculeStr));
  console.log('   - passwordStr:', JSON.stringify(passwordStr));
  
  const requestData = {
    numero_matricule: numeroMatriculeStr,
    password: passwordStr
  };
  
  console.log('📡 api.js - Données de la requête:', JSON.stringify(requestData));
  
  return api.post('/public/membre/login', requestData);
};

// Notifications
export const getNotifications = () => 
  api.get('/notifications');

export const getNotificationsNonLues = () => 
  api.get('/notifications/non-lues');

export const getNotificationsCount = () => 
  api.get('/notifications/count/non-lues');

// Notifications pour les membres (publiques)
export const getMembreNotifications = (numeroMatricule) => 
  api.get(`/public/membre/${numeroMatricule}/notifications`);

export const getMembreNotificationsCount = (numeroMatricule) => 
  api.get(`/public/membre/${numeroMatricule}/notifications/count`);

export const marquerNotificationLue = (id) => 
  api.put(`/notifications/${id}/lu`);

export const marquerToutesLues = () => 
  api.put('/notifications/marquer-toutes-lues');

// Marquer les notifications comme lues pour les membres
export const marquerMembreNotificationLue = (numeroMatricule, id) => 
  api.put(`/public/membre/${numeroMatricule}/notifications/${id}/lu`);

export const marquerToutesMembreNotificationsLues = (numeroMatricule) => 
  api.put(`/public/membre/${numeroMatricule}/notifications/marquer-toutes-lues`);

export const deleteNotification = (id) => 
  api.delete(`/notifications/${id}`);

// Logs (pour débogage)
export const getLogs = (count = 100) => 
  api.get(`/public/logs?count=${count}`);

export const clearLogs = () => 
  api.delete('/public/logs');

// Paramètres du site (page d'accueil, sans auth)
export const getPublicSiteSettings = () => 
  api.get('/public/site-settings');

// Activités du site (page publique /activites)
export const getPublicActivities = () => 
  api.get('/public/activities');

// Gestion des activités du site (admin)
export const getSiteActivities = () => 
  api.get('/site-activities');
export const createSiteActivity = (data) => 
  api.post('/site-activities', data);
export const updateSiteActivity = (id, data) => 
  api.put(`/site-activities/${id}`, data);
export const deleteSiteActivity = (id) => 
  api.delete(`/site-activities/${id}`);
export const uploadActivityImage = (formData) => 
  api.post('/settings/upload-activity-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

// Activités admin
export const getAdminActivities = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.action_type) queryParams.append('action_type', params.action_type);
  if (params.entity_type) queryParams.append('entity_type', params.entity_type);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.offset) queryParams.append('offset', params.offset);
  return api.get(`/admin/activities?${queryParams.toString()}`);
};

// Statistiques hebdomadaires
export const getStatsHebdomadaires = (mois, annee) => {
  return api.get(`/admin/stats/hebdomadaires?mois=${mois}&annee=${annee}`);
};

export const getEvolutionCotisations = (nbMois = 6) => {
  return api.get(`/admin/stats/evolution-cotisations?nbMois=${nbMois}`);
};

export const getComparaisonMois = (mois1, annee1, mois2, annee2) => {
  return api.get(`/admin/stats/comparaison-mois?mois1=${mois1}&annee1=${annee1}&mois2=${mois2}&annee2=${annee2}`);
};

// Routes pour les sauvegardes
export const createBackup = () => api.post('/backup/create');
export const listBackups = () => api.get('/backup/list');
export const downloadBackup = (filename) => api.get(`/backup/download/${filename}`, { responseType: 'blob' });
export const deleteBackup = (filename) => api.delete(`/backup/delete/${filename}`);
export const verifyAdminPassword = (password) => api.post('/backup/verify-password', { password });
export const restoreBackup = (filename, password) => api.post('/backup/restore', { filename, password });
export const getBackupSchedule = () => api.get('/backup/schedule');
export const updateBackupSchedule = (schedule) => api.post('/backup/schedule', schedule);

// Paramètres système
export const getSystemSettings = () => 
  api.get('/settings');

export const getSystemSetting = (key) => 
  api.get(`/settings/${key}`);

export const updateSystemSettings = (settings) => 
  api.put('/settings', { settings });

export const resetSystemSettings = (category = null) => 
  api.post('/settings/reset', { category });

export const uploadLogo = (formData) => 
  api.post('/settings/upload-logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

export const uploadTeamPhoto = (formData) => 
  api.post('/settings/upload-team-photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

export const uploadIndexImage = (formData) => 
  api.post('/settings/upload-index-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

// Notifications SMS
export const getMessageTemplates = () => 
  api.get('/notifications/templates');

export const getMessageTemplate = (id) => 
  api.get(`/notifications/templates/${id}`);

export const createMessageTemplate = (data) => 
  api.post('/notifications/templates', data);

export const updateMessageTemplate = (id, data) => 
  api.put(`/notifications/templates/${id}`, data);

export const deleteMessageTemplate = (id) => 
  api.delete(`/notifications/templates/${id}`);

export const sendNotifications = (data) => 
  api.post('/notifications/send', data);

export const getNotificationHistory = (params = {}) => 
  api.get('/notifications/history', { params });

export const getNotificationDetails = (id) => 
  api.get(`/notifications/history/${id}`);

// Routes Gerer
export const getMembresGerer = () => 
  api.get('/gerer/membres');

export const getMembreGerer = (id) => 
  api.get(`/gerer/membres/${id}`);

export const getFamillesGerer = () => 
  api.get('/gerer/familles');

export const getFamilleGerer = (id) => 
  api.get(`/gerer/familles/${id}`);

export const getRecolteursGerer = () => 
  api.get('/gerer/recolteurs');

// Enfants des membres pour gerer
export const getEnfantsMembreGerer = (membreId) =>
  api.get(`/gerer/membres/${membreId}/enfants`);

export const createEnfantMembreGerer = (membreId, data) => {
  // Si data est FormData, créer une nouvelle instance axios sans le header Content-Type par défaut
  if (data instanceof FormData) {
    const config = {
      headers: {}
    };
    // Ne pas définir Content-Type, axios le fera automatiquement avec le boundary
    return api.post(`/gerer/membres/${membreId}/enfants`, data, config);
  }
  return api.post(`/gerer/membres/${membreId}/enfants`, data);
};

export const getRapportRecolteur = (recolteurId, date = null) => {
  const url = date 
    ? `/gerer/recolteurs/${recolteurId}/rapport?date=${date}`
    : `/gerer/recolteurs/${recolteurId}/rapport`;
  return api.get(url);
};

// Routes Contact
export const sendContactMessage = (data) => 
  api.post('/contact', data);

export const getContactMessages = (params = {}) => 
  api.get('/contact', { params });

export const getContactMessage = (id) => 
  api.get(`/contact/${id}`);

export const updateContactMessageStatus = (id, statut) => 
  api.put(`/contact/${id}/statut`, { statut });

export const addContactMessageResponse = (id, reponse) =>
  api.put(`/contact/${id}/reponse`, { reponse });

/** Renvoyer la réponse par email (depuis infomundes@mundes.org) */
export const sendContactReplyEmail = (id) =>
  api.post(`/contact/${id}/envoyer-email`);

export const deleteContactMessage = (id) =>
  api.delete(`/contact/${id}`);

// Rapports financiers avancés
export const getRapportTresorerie = (params) => 
  api.get('/admin/rapports/tresorerie', { params });

export const getRapportRecouvrement = (params) => 
  api.get('/admin/rapports/recouvrement', { params });

export const getRapportPerformanceRecolteurs = (params) => 
  api.get('/admin/rapports/performance-recolteurs', { params });

export default api;

